import React, { useState, useEffect, useCallback, useRef } from "react";
import ReactDOM from "react-dom";
import { useDispatch, useSelector } from "react-redux";
import { useToasts } from "react-toast-notifications";
import api from "../../api";
import { actions } from "../../redux/_actions";
import utils from "../../utils";

const Signin = ({ isOpen, closeModal }) => {
  const [value, setValue] = useState("");
  const intervalRef = useRef(null);

  const { verifying, secure_identity, error } = useSelector((state) => state.auth);
  const dispatch = useDispatch();

  const { addToast } = useToasts();

  const showToast = useCallback(
    (message, appearance = "error") => {
      addToast(message, { appearance, autoDismiss: true });
    },
    [addToast]
  );

  useEffect(() => {
    if (error) {
      showToast(error, "error");
    }
  }, [error, showToast]);

  useEffect(() => {
    if (!isOpen) return undefined;

    const handleEscape = (event) => {
      if (event.key === "Escape") {
        closeModal();
      }
    };

    document.addEventListener("keydown", handleEscape);
    return () => document.removeEventListener("keydown", handleEscape);
  }, [isOpen, closeModal]);

  useEffect(() => {
    if (!verifying) {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
      return undefined;
    }

    const checkSecureVerified = () => {
      api
        .checkSecureVerified(secure_identity)
        .then((data) => {
          if (data.success) {
            if (intervalRef.current) {
              clearInterval(intervalRef.current);
              intervalRef.current = null;
            }
            dispatch(actions.verifySignin({ uid: secure_identity }));
          }
        })
        .catch((err) => {
          showToast(err.message);
        });
    };

    intervalRef.current = setInterval(checkSecureVerified, 5000);

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    };
  }, [verifying, secure_identity, dispatch, showToast]);

  const signin = (e) => {
    if (e) {
      e.preventDefault();
    }
    if (!value || !value.trim()) {
      showToast("Please enter your email address", "error");
      return;
    }
    if (!utils.validateEmail(value)) {
      showToast("Please enter a valid email address", "error");
      return;
    }
    dispatch(actions.signin({ email: value }));
  };

  if (!isOpen) {
    return null;
  }

  const modal = (
    <div
      className="signup-modal open"
      role="dialog"
      aria-modal="true"
      aria-labelledby="signin-modal-title"
      onClick={(e) => e.stopPropagation()}
    >
      <button
        type="button"
        className="signup-modal__close"
        onClick={closeModal}
        aria-label="Close"
      >
        <span aria-hidden="true">&times;</span>
      </button>
      <div className="background-img">
        <img
          src="/Modal.svg"
          alt=""
          width={1000}
          height={1000}
        />
      </div>
      {verifying ? (
        <div className="verify-container verify-container--banner" role="status">
          Check your email — we sent a secure link to finish signing in.
        </div>
      ) : null}
      <form onSubmit={signin}>
        <div className="headings">
          <div className="logo-heading">
            <img
              src="/ollcart-icon-128-79.png"
              alt="OllaCart"
              width={204}
              height={67}
              style={{ objectFit: "cover" }}
            />
            <h1 id="signin-modal-title">Sign in or create account</h1>
          </div>
          <p>A social way to shop and save</p>
        </div>
        <div className="form-wrap">
          <div className="input-container">
            <label htmlFor="signin-email">Email</label>
            <input
              id="signin-email"
              type="email"
              placeholder="you@example.com"
              value={value}
              onChange={(e) => setValue(e.target.value)}
              autoComplete="email"
              required
            />
          </div>
          <p>
            By continuing, you agree to our Terms and Conditions.
          </p>
        </div>
        <button type="submit" className="button">
          Continue with email
        </button>
      </form>
    </div>
  );

  return ReactDOM.createPortal(modal, document.body);
};

export default Signin;
